/**
 * deriveNextActionCards — distill the Creative Workspace's recommended next
 * steps into 1–4 NextActionCard entries. Each card is independently testable
 * and surfaces in NextActionsStrip.
 *
 * Priority order (capped at 4):
 *   1. Top ranked BriefModSuggestion → "Iterate" card
 *   2. Fatigue warning when frequency ≥ 2.5 OR 7-day ROAS Δ < -0.3
 *   3. Brief outdated (creative.brief_version < brief.latest_version)
 *   4. No active siblings → "Generate more"
 */

import type {
  CreativeSiblingSummary,
  CreativeWorkspaceData,
  NextActionCard,
} from '../types';

const FATIGUE_WARNING_FREQUENCY = 2.5;
const FATIGUE_CRITICAL_FREQUENCY = 3.5;

interface Actions {
  openIterate: (preselectedModIndex?: number) => void;
  openBrief: () => void;
  openIterateNewAngle: () => void;
}

export function deriveNextActionCards(
  data: CreativeWorkspaceData,
  siblings: CreativeSiblingSummary[],
  actions: Actions,
): NextActionCard[] {
  const cards: NextActionCard[] = [];
  const creative = data.creative as Record<string, unknown>;
  const performance = (creative.performance as { lifetime?: Record<string, number> } | undefined)
    ?.lifetime ?? {};
  const ranking = data.ranked_brief_mods as
    | { suggestions?: Array<Record<string, unknown>> }
    | null
    | undefined;
  const series = data.series ?? [];

  // 1. Top ranked brief mod
  const topMod = ranking?.suggestions?.[0];
  if (topMod) {
    const recommendation = topMod.recommendation as
      | { title?: string; rationale?: string }
      | undefined;
    const delta = (topMod.delta as { revenue_impact_delta_usd?: number } | undefined)
      ?.revenue_impact_delta_usd;
    const description =
      (topMod.rank_rationale as string) ||
      recommendation?.rationale ||
      'Apply the highest-ranked PDO modification.';
    cards.push({
      type: 'iterate',
      title: recommendation?.title ?? 'Test a creative variant',
      description,
      estimatedImpact:
        delta != null && delta > 0
          ? `+$${delta.toLocaleString(undefined, { maximumFractionDigits: 0 })} est.`
          : undefined,
      cta: 'Iterate →',
      onAction: () => actions.openIterate(0),
    });
  }

  // 2. Fatigue warning
  const frequency = Number(performance.frequency ?? 0) || 0;
  const roas7dDelta = compute7dRoasDelta(series);
  const fatigueTriggered =
    frequency >= FATIGUE_WARNING_FREQUENCY || (roas7dDelta != null && roas7dDelta < -0.3);
  if (fatigueTriggered) {
    const isFatigued = frequency >= FATIGUE_CRITICAL_FREQUENCY;
    const title = isFatigued
      ? `Frequency ${frequency.toFixed(1)} — rotation needed`
      : roas7dDelta != null && roas7dDelta < -0.3
        ? 'ROAS declining — possible creative fatigue'
        : `Frequency ${frequency.toFixed(1)} — approaching limit`;
    const description = isFatigued
      ? 'Audience has seen this ad too many times. Generate a fresh variant before performance degrades further.'
      : 'ROAS is declining week-over-week. A new creative from the same angle may reset performance.';
    cards.push({
      type: 'fatigue_warning',
      title,
      description,
      cta: 'Generate fresh →',
      onAction: () => actions.openIterate(),
    });
  }

  // 3. Brief outdated
  const briefVersion = creative.brief_version as number | null | undefined;
  if (
    data.brief &&
    briefVersion != null &&
    briefVersion < (data.brief.latest_version ?? data.brief.version)
  ) {
    cards.push({
      type: 'rev_brief',
      title: `Brief updated to v${data.brief.latest_version}`,
      description:
        'This creative was generated from an older brief version. Consider generating a new variant from the current brief.',
      cta: 'View brief →',
      onAction: () => actions.openBrief(),
    });
  }

  // 4. No active siblings → generate more
  const activeSiblings = siblings.filter(
    (s) => !s.is_current && (s.status === 'active' || s.status === 'paused'),
  );
  if (activeSiblings.length === 0 && siblings.length <= 1) {
    cards.push({
      type: 'generate_more',
      title: 'No sibling variants yet',
      description: 'Generate 2–3 variations from this angle to build an A/B test pool.',
      cta: 'Generate →',
      onAction: () => actions.openIterateNewAngle(),
    });
  }

  return cards.slice(0, 4);
}

function compute7dRoasDelta(
  series: Array<{ roas?: number; date?: string }>,
): number | null {
  if (!series || series.length < 14) return null;
  const last7 = series.slice(-7);
  const prev7 = series.slice(-14, -7);
  const avg = (rows: typeof series) =>
    rows.length === 0 ? 0 : rows.reduce((s, r) => s + (Number(r.roas) || 0), 0) / rows.length;
  return avg(last7) - avg(prev7);
}
